import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Tooltip,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Chip,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    ContentCopy as ContentCopyIcon,
    Info as InfoIcon,
    Block as BlockIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { flowService } from '../services/flowService';
import { Flow } from '../types/flow';
import { format } from 'date-fns';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';
import { alpha, useTheme } from '@mui/material/styles';

export const FlowList = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [flows, setFlows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const { showSuccess, showError } = useNotification();
    const [newFlowDialogOpen, setNewFlowDialogOpen] = useState(false);
    const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
    const [selectedFlow, setSelectedFlow] = useState(null);
    const [flowName, setFlowName] = useState('');
    const [loading, setLoading] = useState(false);
    const [cloningFlowId, setCloningFlowId] = useState(null);

    useEffect(() => {
        loadFlows();
    }, []);

    const loadFlows = async () => {
        try {
            const data = await flowService.list();
            console.log('Fluxos carregados:', data);
            
            // Garante que todos os fluxos tenham a propriedade available definida
            const processedFlows = data.map(flow => ({
                ...flow,
                available: flow.available === undefined ? true : flow.available
            }));
            
            // Ordenar fluxos alfabeticamente pelo nome
            const sortedFlows = [...processedFlows].sort((a, b) => {
                if (!a.name) return 1;
                if (!b.name) return -1;
                return a.name.localeCompare(b.name, 'pt-BR');
            });
            
            console.log('Fluxos ordenados alfabeticamente:', sortedFlows);
            setFlows(sortedFlows);
        } catch (error) {
            console.error('Erro ao carregar fluxos:', error);
            showError('Erro ao carregar os fluxos. Por favor, tente novamente.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este fluxo?')) {
            try {
                await flowService.delete(id);
                loadFlows();
                showSuccess('Fluxo excluído com sucesso!');
            } catch (error) {
                console.error('Erro ao excluir fluxo:', error);
                showError('Erro ao excluir o fluxo. Por favor, tente novamente.');
            }
        }
    };

    const handleToggleActive = async (flow) => {
        try {
            console.log('Estado atual do fluxo:', flow);
            // Considera available como true se for undefined
            const currentAvailable = flow.available === undefined ? true : Boolean(flow.available);
            const newAvailableState = !currentAvailable;
            console.log('Alterando available para:', newAvailableState);

            // Envia apenas os campos necessários para a atualização
            const updatedFlow = {
                available: newAvailableState
            };

            console.log('Dados a serem enviados:', updatedFlow);
            
            // Faz a atualização
            await flowService.update(flow.id, updatedFlow);
            
            // Atualiza o estado local para refletir a mudança imediatamente
            setFlows(flows.map(f => 
                f.id === flow.id ? { ...f, available: newAvailableState } : f
            ));
            
            // Mostra mensagem de sucesso
            showSuccess(`Fluxo ${newAvailableState ? 'ativado' : 'desativado'} com sucesso!`);
        } catch (error) {
            console.error('Erro ao alterar disponibilidade do fluxo:', error);
            showError('Erro ao alterar disponibilidade do fluxo. Por favor, tente novamente.');
        }
    };

    const handleToggleIsActive = async (flow) => {
        try {
            console.log('Estado atual do fluxo (is_active):', flow);
            // Considera is_active como true se for undefined
            const currentIsActive = flow.is_active === undefined ? true : Boolean(flow.is_active);
            const newIsActiveState = !currentIsActive;
            console.log('Alterando is_active para:', newIsActiveState);

            // Envia apenas os campos necessários para a atualização
            const updatedFlow = {
                is_active: newIsActiveState
            };

            console.log('Dados a serem enviados:', updatedFlow);
            
            // Faz a atualização
            await flowService.update(flow.id, updatedFlow);
            
            // Atualiza o estado local para refletir a mudança imediatamente
            setFlows(flows.map(f => 
                f.id === flow.id ? { ...f, is_active: newIsActiveState } : f
            ));
            
            // Mostra mensagem de sucesso
            showSuccess(`Fluxo ${newIsActiveState ? 'ativado' : 'desativado'} com sucesso!`);
        } catch (error) {
            console.error('Erro ao alterar status do fluxo:', error);
            showError('Erro ao alterar status do fluxo. Por favor, tente novamente.');
        }
    };

    const handleClone = async (flow) => {
        try {
            setCloningFlowId(flow.id);
            console.log('Clonando fluxo:', flow.id);

            // Usar o endpoint específico de clonagem
            const response = await api.post(`/flows-new/${flow.id}/clone`, {
                new_name: `${flow.name} (Cópia)`
            });
            
            const newFlow = response.data;
            console.log('Fluxo clonado com sucesso:', newFlow);

            showSuccess('Fluxo clonado com sucesso!');
            
            // Recarregar a lista de fluxos
            loadFlows();
            
            // Opcionalmente, navegar para o novo fluxo
            // navigate(`/flows-new/${newFlow.id}/edit`);
        } catch (error) {
            console.error('Erro ao clonar fluxo:', error);
            showError(`Erro ao clonar fluxo: ${error.message || 'Erro desconhecido'}`);
        } finally {
            setCloningFlowId(null);
        }
    };

    const handleNewFlow = () => {
        setFlowName('');
        setNewFlowDialogOpen(true);
    };

    const handleEditName = (flow) => {
        setSelectedFlow(flow);
        setFlowName(flow.name);
        setEditNameDialogOpen(true);
    };

    const handleSaveNewFlow = async () => {
        if (!flowName.trim()) {
            showError('O nome do fluxo é obrigatório');
            return;
        }

        setLoading(true);
        try {
            const newFlow = await flowService.create({
                name: flowName.trim(),
                is_active: true,
                available: true,
                questions: []
            });
            showSuccess('Fluxo criado com sucesso!');
            setNewFlowDialogOpen(false);
            navigate(`/flows-new/${newFlow.id}/edit`);
        } catch (error) {
            console.error('Erro ao criar fluxo:', error);
            showError('Erro ao criar o fluxo. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEditName = async () => {
        if (!flowName.trim()) {
            showError('O nome do fluxo é obrigatório');
            return;
        }

        setLoading(true);
        try {
            // Primeiro, obter o fluxo completo do backend para garantir que temos todas as questões
            console.log('Obtendo fluxo completo antes de renomear:', selectedFlow.id);
            const completeFlow = await flowService.get(selectedFlow.id);
            
            // Atualizar apenas o nome, mantendo todas as questões e conexões
            const updatedFlow = {
                ...completeFlow,
                name: flowName.trim()
            };
            
            console.log('Atualizando fluxo com todas as questões preservadas:', updatedFlow);
            await flowService.update(selectedFlow.id, updatedFlow);
            
            showSuccess('Nome do fluxo atualizado com sucesso!');
            setEditNameDialogOpen(false);
            loadFlows();
        } catch (error) {
            console.error('Erro ao atualizar nome do fluxo:', error);
            showError('Erro ao atualizar nome do fluxo. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Filtrar fluxos com base no termo de busca
    const filteredFlows = flows.filter(flow => 
        flow.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Limpar a busca
    const handleClearSearch = () => {
        setSearchTerm('');
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ my: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Fluxos
                    </Typography>
                    <Box>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleNewFlow}
                        >
                            Novo Fluxo
                        </Button>
                    </Box>
                </Box>

                {/* Campo de busca */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Buscar fluxos por nome..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="limpar busca"
                                        onClick={handleClearSearch}
                                        edge="end"
                                        size="small"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: 2,
                                bgcolor: theme.palette.mode === 'dark' 
                                    ? alpha(theme.palette.background.paper, 0.8) 
                                    : alpha(theme.palette.background.paper, 0.9),
                                '&:hover': {
                                    bgcolor: theme.palette.background.paper
                                },
                                boxShadow: theme.shadows[1]
                            }
                        }}
                    />
                    {searchTerm && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                {filteredFlows.length === 0 
                                    ? 'Nenhum resultado encontrado' 
                                    : `${filteredFlows.length} ${filteredFlows.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}`
                                }
                            </Typography>
                            <Button 
                                size="small" 
                                onClick={handleClearSearch}
                                startIcon={<ClearIcon fontSize="small" />}
                                sx={{ py: 0 }}
                            >
                                Limpar busca
                            </Button>
                        </Box>
                    )}
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '35%' }}>Nome</TableCell>
                                <TableCell sx={{ width: '15%' }}>Status Geral</TableCell>
                                <TableCell sx={{ width: '15%' }}>
                                    <Tooltip title="Controla se o fluxo aparece na lista de tipos de ato">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            Disponível
                                            <InfoIcon fontSize="small" sx={{ ml: 0.5, fontSize: '16px', color: 'text.secondary' }} />
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ width: '15%' }}>Criado em</TableCell>
                                <TableCell sx={{ width: '20%' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredFlows.map((flow) => (
                                <TableRow 
                                    key={flow.id}
                                    sx={{
                                        backgroundColor: !flow.available ? 
                                            (theme) => alpha(theme.palette.error.light, 0.15) : 
                                            'inherit',
                                        opacity: !flow.available ? 0.8 : 1
                                    }}
                                >
                                    <TableCell sx={{ width: '35%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {flow.name}
                                            <Tooltip title="Editar nome">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditName(flow)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: '15%' }}>
                                        {flow.available ? (
                                            <Chip 
                                                label="Disponível" 
                                                color="success" 
                                                size="small" 
                                                sx={{ fontWeight: 500 }}
                                            />
                                        ) : (
                                            <Chip 
                                                label="Indisponível" 
                                                color="error" 
                                                size="small" 
                                                sx={{ fontWeight: 500, opacity: 0.9 }}
                                                icon={<BlockIcon fontSize="small" />}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ width: '15%' }}>
                                        <Tooltip title="Disponibilidade: Controla se o fluxo aparece na lista de tipos de ato">
                                            <Switch
                                                checked={flow.available === undefined ? true : Boolean(flow.available)}
                                                onChange={() => handleToggleActive(flow)}
                                                color="primary"
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ width: '15%' }}>
                                        {format(new Date(flow.created_at), 'dd/MM/yyyy HH:mm')}
                                    </TableCell>
                                    <TableCell sx={{ width: '20%' }}>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start' }}>
                                            <Tooltip title="Visualizar">
                                                <IconButton
                                                    onClick={() => navigate(`/flows-new/${flow.id}/preview`)}
                                                    size="small"
                                                    color="info"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                                <IconButton
                                                    onClick={() => navigate(`/flows-new/${flow.id}/edit`)}
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Clonar">
                                                <IconButton
                                                    onClick={() => handleClone(flow)}
                                                    size="small"
                                                    color="primary"
                                                    disabled={cloningFlowId === flow.id}
                                                >
                                                    {cloningFlowId === flow.id ? <CircularProgress size={24} /> : <ContentCopyIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir">
                                                <IconButton
                                                    onClick={() => handleDelete(flow.id)}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredFlows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                            {searchTerm ? (
                                                <>
                                                    <SearchIcon color="action" sx={{ fontSize: 40, opacity: 0.5 }} />
                                                    <Typography variant="h6" color="textSecondary">
                                                        Nenhum fluxo encontrado para "{searchTerm}"
                                                    </Typography>
                                                    <Button 
                                                        variant="outlined" 
                                                        size="small" 
                                                        onClick={handleClearSearch}
                                                        startIcon={<ClearIcon />}
                                                    >
                                                        Limpar busca
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <InfoIcon color="info" sx={{ fontSize: 40, opacity: 0.7 }} />
                                                    <Typography variant="h6" color="textSecondary">
                                                        Nenhum fluxo disponível
                                                    </Typography>
                                                    <Button 
                                                        variant="contained" 
                                                        color="primary" 
                                                        startIcon={<AddIcon />}
                                                        onClick={handleNewFlow}
                                                    >
                                                        Criar um fluxo
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Modal para novo fluxo */}
            <Dialog 
                open={newFlowDialogOpen} 
                onClose={() => setNewFlowDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Novo Fluxo</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nome do Fluxo"
                        type="text"
                        fullWidth
                        value={flowName}
                        onChange={(e) => setFlowName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewFlowDialogOpen(false)}>Cancelar</Button>
                    <Button 
                        onClick={handleSaveNewFlow} 
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal para editar nome */}
            <Dialog 
                open={editNameDialogOpen} 
                onClose={() => setEditNameDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Editar Nome do Fluxo</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nome do Fluxo"
                        type="text"
                        fullWidth
                        value={flowName}
                        onChange={(e) => setFlowName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditNameDialogOpen(false)}>Cancelar</Button>
                    <Button 
                        onClick={handleSaveEditName} 
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}; 